import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "../../App.css";
import shishi1 from "../../assets/images/shishi1.jpg";
import shishi2 from "../../assets/images/shishi2.jpg";
import shishi3 from "../../assets/images/shishi3.jpg";
import shishi4 from "../../assets/images/shishi4.jpg";

const JAPAN_IMAGES = [shishi1, shishi2, shishi3, shishi4];

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 120 : -120,
    opacity: 0,
    scale: 0.96,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -120 : 120,
    opacity: 0,
    scale: 0.96,
  }),
};

export default function About() {
  const [slideIndex, setSlideIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const goToSlide = (index) => {
    setDirection(index > slideIndex ? 1 : -1);
    setSlideIndex(index);
  };
  const goPrev = () => {
    setDirection(-1);
    setSlideIndex(
      (prev) => (prev - 1 + JAPAN_IMAGES.length) % JAPAN_IMAGES.length,
    );
  };
  const goNext = () => {
    setDirection(1);
    setSlideIndex((prev) => (prev + 1) % JAPAN_IMAGES.length);
  };

  const getDate = () => {
    var dob = new Date("09/11/2000");
    var month_diff = Date.now() - dob.getTime();
    var age_dt = new Date(month_diff);
    var year = age_dt.getUTCFullYear();
    var age = Math.abs(year - 1970);
    return age;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const stats = [
    { number: "5+", label: "Years Coding" },
    { number: "20+", label: "Projects Built" },
    { number: "6+ ", label: "Hackathons" },
    { number: "2+", label: "Awards" },
  ];

  return (
    <div
      id="about"
      ref={ref}
      className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex flex-col text-center gap-8 text-gray-800 font-normal py-20 px-4 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100 rounded-full blur-3xl opacity-30"></div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10 max-w-7xl mx-auto"
      >
        <motion.div variants={itemVariants} className="mb-16">
          <h2 className="head text-5xl md:text-6xl lg:text-7xl font-bold mb-4 gradient-text">
            About Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="glass p-6 rounded-2xl text-center"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="text-3xl md:text-4xl font-bold gradient-text mb-2"
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : { scale: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {stat.number}
              </motion.div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content Section */}
        <div className="grid lg:grid-cols-3 gap-12 items-start">
          {/* Left Column - About Text */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-2 text-left space-y-6"
          >
            <motion.h3
              className="text-3xl md:text-4xl font-bold leading-tight"
              whileHover={{ scale: 1.02 }}
            >
              I'm <span className="gradient-text">Khashayar Moradpour,</span> a
              Software Developer & Recent Graduate
            </motion.h3>

            <motion.div
              className="space-y-6 text-lg leading-relaxed text-gray-700"
              variants={containerVariants}
            >
              <motion.p variants={itemVariants}>
                I'm Khashayar Moradpour, a {getDate()}-year-old recent Computer
                Science graduate from San Jose State University. With five years
                of programming experience, I'm passionate about creating
                applications that make a positive impact on the world around me.
                Whether it's building with React, Next.js, Django, or Spring
                Boot, I love tackling challenges and bringing meaningful ideas
                to life.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="glass-dark p-6 rounded-2xl bg-gradient-to-r from-blue-50 to-purple-50"
              >
                <h4 className="font-bold text-xl mb-3 gradient-text">
                  Current Project Spotlight
                </h4>
                <p className="text-gray-700">
                  One of my current projects is <strong>Mood Stabilizer</strong>
                  , an app designed to help people track and manage their
                  emotions. It features a journaling tool backed by a SQL
                  database, smart responses powered by the Gemini API, and
                  text-to-speech functionality through OpenAI, making it both
                  engaging and accessible.
                </p>
              </motion.div>

              <motion.p variants={itemVariants}>
                Outside of coding, I love hitting the volleyball court and
                thrive in team settings—whether that's spiking on the court or
                collaborating with others to solve complex problems. I'm always
                eager to learn, grow, and be part of something great by
                building applications that truly make a
                difference.
              </motion.p>

              <motion.div variants={itemVariants} className="space-y-3">
                <p className="text-gray-700">
                  I'm also big on exploring new places and cultures keeps me
                  inspired. A while back, my girlfriend and I took a trip to
                  Japan. Here are some pictures from that trip:
                </p>
                {/* Japan trip slideshow — manual only, no auto-advance */}
                <div className="mt-4 max-w-md mx-auto">
                  {/* Polaroid-style frame with soft shadow and tilt */}
                  <motion.div
                    className="relative bg-white px-4 pt-4 pb-12 shadow-[0_4px_20px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]"
                    style={{
                      boxShadow:
                        "0 4px 24px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.06)",
                    }}
                    initial={{ rotate: -0.8 }}
                    whileHover={{ rotate: 0, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  >
                    {/* Small "Japan" label */}
                    <div className="absolute -top-1 right-5 px-2 py-0.5 bg-gradient-to-r from-rose-400 to-amber-400 text-white text-[11px] font-semibold tracking-wider rounded-sm shadow z-10">
                      JAPAN
                    </div>
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      <AnimatePresence
                        mode="wait"
                        initial={false}
                        custom={direction}
                      >
                        <motion.img
                          key={slideIndex}
                          src={JAPAN_IMAGES[slideIndex]}
                          alt={`Japan trip — photo ${slideIndex + 1}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          custom={direction}
                          variants={slideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{
                            type: "spring",
                            stiffness: 320,
                            damping: 32,
                          }}
                        />
                      </AnimatePresence>
                      {/* Prev / Next — minimal pill buttons */}
                      <button
                        type="button"
                        onClick={goPrev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
                        aria-label="Previous photo"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
                        aria-label="Next photo"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="text-center text-gray-500 text-xs mt-3 font-medium">
                      Photo {slideIndex + 1} of {JAPAN_IMAGES.length}
                    </p>
                  </motion.div>
                  {/* Thumbnail strip — click to jump */}
                  <div className="flex justify-center gap-2 mt-3">
                    {JAPAN_IMAGES.map((img, i) => (
                      <motion.button
                        key={i}
                        type="button"
                        onClick={() => goToSlide(i)}
                        className={`relative rounded-lg overflow-hidden flex-shrink-0 w-14 h-10 border-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                          i === slideIndex
                            ? "border-blue-500 shadow-md scale-105"
                            : "border-transparent opacity-75 hover:opacity-100"
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        aria-label={`Go to photo ${i + 1}`}
                      >
                        <img
                          src={img}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        {i === slideIndex && (
                          <span className="absolute inset-0 bg-blue-500/20 pointer-events-none" />
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.p variants={itemVariants} className="text-gray-600">
                As a recent graduate, I am actively seeking full-time software
                development opportunities to apply my skills and continue
                growing in the field. With a strong background in React.js,
                Next.js, Python, and other technologies listed on my{" "}
                <a
                  className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                  href="#resume"
                >
                  resume
                </a>
                , I have actively participated in numerous{" "}
                <a
                  className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                  href="https://devpost.com/woddyowl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  hackathons
                </a>
                . I am also keen to engage in collaborative efforts within
                open-source projects.
              </motion.p>
            </motion.div>
          </motion.div>

          {/* Right Column - Animation and Resume Button */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-1 flex flex-col items-center space-y-8"
          >
            {/* Lottie Animation */}
            <motion.div
              className="relative w-full max-w-sm"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <lottie-player
                autoplay
                loop
                mode="bounce"
                src="https://assets10.lottiefiles.com/packages/lf20_w98qte06.json"
                style={{ width: "100%", height: "300px" }}
              />

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-pink-400 to-yellow-400 rounded-full opacity-20 blur-xl"></div>
            </motion.div>

            {/* Resume Button */}
            <motion.div
              className="w-full flex flex-col items-center space-y-4"
              variants={itemVariants}
            >
              <motion.button
                className="btn-primary w-full max-w-xs"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/K.Moradpour-Résumé.pdf"
                  className="flex items-center justify-center gap-2 w-full"
                >
                  <span>View Resume</span>
                  <motion.span
                    animate={{ rotate: [0, 360] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    📄
                  </motion.span>
                </a>
              </motion.button>

              {/* Additional Info Card */}
              <motion.div
                className="glass p-4 rounded-xl text-center max-w-xs w-full"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <motion.div
                    className="w-2 h-2 bg-green-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Available for Opportunities
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Open to full-time software development positions
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
